import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Modal, Button, ModalFooter } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { CalloutContext, useOkapiKy, useStripes } from '@folio/stripes/core';
import ScheduleForm from './ScheduleForm';
import useScheduledReportsMutations from '../../hooks/useScheduledReportsMutations';

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Build ISO startDate from form fields using tenant timezone.
 * Converts user-entered local time to UTC via moment-timezone.
 * Same pattern as ui-checkin buildDateTime().
 */
const buildStartDate = (formData, timezone) => {
  if (!formData.startDate) return null;
  const tz = timezone || 'UTC';
  const date = moment.tz(String(formData.startDate), tz).format("YYYY-MM-DD");

  if (formData.frequency === 'custom') {
    return moment.tz(date, tz).toISOString();
  }

  if (formData.frequency === 'once') {
    const time = (formData.executionTime || '09:00:00').substring(0, 5);
    return moment.tz(`${date}T${time}`, tz).toISOString();
  }

  if (formData.frequency === 'hourly') {
    const mm = String(formData.executionMinute ?? '0').padStart(2, '0');
    return moment.tz(`${date}T00:${mm}`, tz).toISOString();
  }

  const time = (formData.executionTime || '09:00:00').substring(0, 5);

  if (formData.frequency === 'weekly') {
    const DOW_MAP = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
    const targetDay = DOW_MAP[formData.dayOfWeek ?? 'MON'];
    const m = moment.tz(`${date}T${time}`, tz);
    const diff = (targetDay - m.day() + 7) % 7 || 7;
    return m.add(diff, 'days').toISOString();
  }

  if (formData.frequency === 'monthly') {
    const dom = parseInt(formData.dayOfMonth ?? '1', 10);
    const m = moment.tz(`${date}T${time}`, tz).date(dom);
    if (m.isBefore(moment())) m.add(1, 'month');
    return m.toISOString();
  }

  return moment.tz(`${date}T${time}`, tz).toISOString();
};

/**
 * Build ISO endDate from form fields using tenant timezone.
 * Defaults to end of day (23:59) if no endTime specified.
 */
const buildEndDate = (formData, timezone) => {
  if (!formData.endDate) return null;
  const tz = timezone || 'UTC';
  const date = moment.tz(String(formData.endDate), tz).format("YYYY-MM-DD");
  const time = (formData.endTime || '23:59:00').substring(0, 5);
  return moment.tz(`${date}T${time}`, tz).toISOString();
};

const parseLocalized = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value.trim() ? [{ locale: 'en', value }] : [];
  }
};

// Today's date in YYYY-MM-DD format for Datepicker default
const getTodayDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DEFAULT_INITIAL = {
  frequency:       'daily',
  executionTime:   '09:00:00',
  executionMinute: '0',
  dayOfWeek:       'MON',
  dayOfMonth:      '1',
  outputFormat:    'excel',
  endTime:         '23:59:00',
  startDate:       getTodayDate(),
  recipients:      [{ email: '', name: '', locale: 'ar' }],
  name:            [{ locale: 'en', value: '' }],
  description:     [],
  emailSubject:    [{ locale: 'en', value: '' }],
  emailBody:       [],
};

// ── Component ──────────────────────────────────────────────────────────────

function ScheduleReportModal({
  open,
  onClose,
  reportId,
  reportName,
  onSuccess,
}) {
  const { createSchedule } = useScheduledReportsMutations();
  const callout = useContext(CalloutContext);
  const intl = useIntl();
  const okapiKy = useOkapiKy();
  const { timezone } = useStripes();
  const okapiKyRef = useRef(okapiKy);
  useEffect(() => { okapiKyRef.current = okapiKy; }, [okapiKy]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState({ ...DEFAULT_INITIAL });
  const formRef = useRef(null);

  // Fetch report + email templates together when modal opens
  useEffect(() => {
    if (!open || !reportId) return;

    Promise.all([
      okapiKyRef.current.get(`reports/${reportId}`).json(),
      okapiKyRef.current
        .get('reportsConfigs?query=(configCode=="DEFAULT_EMAIL_SUBJECT" or configCode=="DEFAULT_EMAIL_BODY")&limit=2')
        .json(),
    ])
      .then(([report, configRes]) => {
        const nameArr = Array.isArray(report?.name) ? report.name : [];
        const configs = configRes?.reportsConfigs || [];

        // Helper: get report name for a specific locale
        const getReportName = (locale) => {
          if (!nameArr.length) return '';
          const lang = locale?.split('-')[0];
          const match = nameArr.find(n => n.locale === locale)
            || nameArr.find(n => n.locale?.split('-')[0] === lang);
          return match?.value || '';
        };

        // Start with empty subject/body — will be filled from saved templates
        let subject = [];
        let body = [];

        // Fill from saved templates, replacing {reportName} per locale
        configs.forEach(c => {
          if (c.configCode === 'DEFAULT_EMAIL_SUBJECT') {
            const parsed = parseLocalized(c.value);
            if (parsed.length) {
              subject = parsed.map(item => ({
                ...item,
                value: item.value.replace(/{reportName}/g, getReportName(item.locale)),
              }));
            }
          }
          if (c.configCode === 'DEFAULT_EMAIL_BODY') {
            const parsed = parseLocalized(c.value);
            if (parsed.length) {
              body = parsed.map(item => ({
                ...item,
                value: item.value.replace(/{reportName}/g, getReportName(item.locale)),
              }));
            }
          }
        });

        setInitialValues(prev => ({
          ...prev,
          name:         nameArr,
          emailSubject: subject,
          emailBody:    body,
        }));
      })
      .catch(() => {});
  }, [open, reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        reportId,
        name: formData.name || [],
        frequency: formData.frequency,
        outputFormat: formData.outputFormat,
        recipients: formData.recipients || [],
        isActive: true,
        startDate: buildStartDate(formData, timezone),
      };

      if (formData.description?.length)
        payload.description = formData.description;
      if (formData.emailSubject?.length)
        payload.emailSubject = formData.emailSubject;
      if (formData.emailBody?.length) payload.emailBody = formData.emailBody;
      if (formData.frequency === 'custom' && formData.cronExpression)
        payload.cronExpression = formData.cronExpression;

      const endDate = buildEndDate(formData, timezone);
      if (endDate) payload.endDate = endDate;

      await createSchedule.mutateAsync(payload);

      callout.sendCallout({
        message: intl.formatMessage({
          id: 'ui-reports.scheduledReports.callout.created',
        }),
        type: 'success',
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      callout.sendCallout({
        message: intl.formatMessage(
          { id: 'ui-reports.scheduledReports.callout.createError' },
          { error: error.message },
        ),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      label={
        <FormattedMessage
          id="ui-reports.scheduledReports.modal.title"
          values={{
            reportName: Array.isArray(reportName)
              ? reportName.find((n) => n.locale?.startsWith('ar'))?.value ||
                reportName[0]?.value ||
                ''
              : reportName || '',
          }}
        />
      }
      footer={
        <ModalFooter>
          <Button
            buttonStyle="primary"
            disabled={isSubmitting}
            marginBottom0
            onClick={() => formRef.current && formRef.current.submit()}
          >
            <FormattedMessage id="ui-reports.scheduledReports.create" />
          </Button>
          <Button onClick={onClose} disabled={isSubmitting} marginBottom0>
            <FormattedMessage id="ui-reports.button.cancel" />
          </Button>
        </ModalFooter>
      }
      onClose={onClose}
      size="large"
    >
      <ScheduleForm
        onSubmit={handleSubmit}
        initialValues={initialValues}
        formRef={formRef}
      />
    </Modal>
  );
}

ScheduleReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reportId: PropTypes.string.isRequired,
  reportName: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSuccess: PropTypes.func,
};

export default ScheduleReportModal;
